import {
  Flex, Icon,
} from '@equifinance/shared';
import styled from 'styled-components';
import {
  DragEvent, ChangeEvent, useRef,
  useState,
} from 'react';
import { LoadingSpinner } from './Icons/LoadingSpinner';

const UploadWrapper = styled.div<{ disabled: boolean }>`${({ disabled }) => `
  position: relative;
  opacity: ${disabled ? 0.5 : 1};
`}`;

const LoadingContainer = styled(Flex)`${({ theme }) => `
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 100%;
  border-radius: ${theme.borderRadius[2]};
  align-items: center;
  justify-content: center;
  background: ${theme.colours.neutral[8]};
  flex-direction: column;
`}`;

const UploadArea = styled(Flex) <{ isDragging?: boolean, disabled?: boolean }>`${({ theme, isDragging = false, disabled = false }) => `
  position: relative;
  border: ${theme.borderWidth[1]} dashed ${theme.colours.neutral[5]};
  border-radius: ${theme.borderRadius[2]};
  background: ${(isDragging && !disabled) && theme.colours.neutral[9]};
  padding: ${theme.padding[3]} ${theme.padding[6]};
  flex-direction: column;
  align-items: center;
  gap: ${theme.padding[2]};
  
  &:hover {
    background: ${!disabled && theme.colours.neutral[9]};
  }
`}`;

const HiddenInput = styled.input`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  opacity: 0;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

const UploadIcon = styled(Icon).attrs(() => ({
  className: 'mi-cloud-upload',
}))`${({ theme }) => `
  font-size:${theme.fontSize[3]};
`}`;

const UploadText = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[2]};
  color: ${theme.colours.neutral[2]};
  font-size: ${theme.fontSize[2]};
`}`;

const BrowseText = styled.span`${({ theme }) => `
  color: ${theme.colours.link[1]};
  cursor: pointer;
`}`;

const Strong = styled.strong``;

const UploadInfo = styled.div`${({ theme }) => `
  color: ${theme.colours.neutral[3]};
  font-size: ${theme.fontSize[1]};
`}`;

type UploadFileAreaProps = {
  supportedFileTypes: string[],
  maxFileSize: number,
  maxFilesNumber: number,
  onUploadFiles(files: FileList): Promise<void>;
  disabled?: boolean,
}

export default function UploadFileArea({
  supportedFileTypes,
  maxFileSize,
  maxFilesNumber,
  onUploadFiles,
  disabled = false,
}: UploadFileAreaProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const acceptedFileTypes = supportedFileTypes.map((type) => `.${type}`).join(',');
  const fileSizeMB = maxFileSize / 1000 / 1000;

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragEnter = () => {
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleFiles = async (files: FileList) => {
    try {
      setIsUploading(true);
      await onUploadFiles(files);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled || !e.dataTransfer.files?.length) return;
    handleFiles(e.dataTransfer.files);
  };

  const handleBrowseClick = async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || !e.target.files?.length) return;
    handleFiles(e.target.files);
  };

  return (
    <UploadWrapper disabled={disabled}>
      {isUploading && (<LoadingContainer><LoadingSpinner /></LoadingContainer>)}
      <UploadArea
        onDrop={handleDrop}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        isDragging={isDragging}
        disabled={disabled}
      >
        <HiddenInput
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleBrowseClick}
          accept={acceptedFileTypes}
          onFocus={resetFileInput}
          disabled={disabled}
        />
        <UploadText>
          <UploadIcon />
          {'Drag and drop here or '}
          <BrowseText>browse</BrowseText>
        </UploadText>
        <UploadInfo>
          {'Max file size '}
          <Strong>{`${fileSizeMB} MB.`}</Strong>
          {' Max file numbers '}
          <Strong>{`${maxFilesNumber} files.`}</Strong>
          {' Supported file types: '}
          <Strong>{supportedFileTypes.join(', ')}</Strong>
        </UploadInfo>
      </UploadArea>
    </UploadWrapper>
  );
}
